import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import IconButton from "../../../components/ui/IconButton";
import Icon from "../../../components/ui/Icon";
import Calendar from "../calendar/Calendar";
import styles from "./SalaryCenter.module.css";
import { computePayroll, dailyRateFromSalary } from "../payrollMath";
import { exportPayslipPDF } from "../payslipPdf";
import { useCompanyDefaults, resolveEmployeeWorkSettings } from "../../../utils/companyDefaults";

// Locale-friendly month label for the empty-timesheet banner. Falls
// back to "the month" if the index is out of range so the banner
// never reads "January NaN" on a malformed selectedMonth.
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const peso = (n) => `₱${Number(n || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

export default function SalaryCenter({
  calendarDays,
  selectedMonth,
  selectedYear,
  onMonthChange,
  onYearChange,
  selectedEmployee,
  timesheetSummary = { worked: 0, overtime: 0, leave: 0 },
  timesheets = [],
}) {
  // SPA navigation for the "Open Timesheet" CTA on the empty-timesheet
  // banner — keeps the session alive (sessionStorage auth) instead of
  // doing a full page reload that bounces through /login.
  const navigate = useNavigate();

  // Remember the user's hide/show choice across reloads.
  const [salaryHidden, setSalaryHidden] = useState(
    () => localStorage.getItem("salaryHidden") === "1"
  );
  // Collapse/expand the payroll computation detail rows. Defaults to
  // expanded on first visit; choice persists in localStorage.
  const [payrollCollapsed, setPayrollCollapsed] = useState(
    () => localStorage.getItem("payrollCollapsed") === "1"
  );
  const togglePayroll = () => {
    setPayrollCollapsed(prev => {
      const next = !prev;
      localStorage.setItem("payrollCollapsed", next ? "1" : "0");
      return next;
    });
  };
  const toggleSalary = () => {
    setSalaryHidden(prev => {
      const next = !prev;
      localStorage.setItem("salaryHidden", next ? "1" : "0");
      return next;
    });
  };

  // "Salary not configured" gate — HR forgot (or hasn't yet) typed a
  // base salary on the People → Edit Employee form. Previously the
  // payroll computation silently fell back to the company default
  // (₱700/day → ₱15,400 monthly), which made the Payroll Computation
  // card claim a phantom paycheck for an employee whose Based Salary
  // header read "—". That was a real "phantom paycheck" bug. We now
  // treat <=0 / null / NaN salary as an explicit unconfigured state
  // and surface it everywhere on this page: warning banner, zero rows,
  // disabled payslip export.
  const salaryNumber = Number(selectedEmployee?.salary);
  const salaryConfigured = Number.isFinite(salaryNumber) && salaryNumber > 0;

  const salary = salaryConfigured
    ? `₱${salaryNumber.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "—";

  // Daily rate is derived from the employee's base (monthly) salary:
  //   dailyRate = salary / WORK_DAYS_PER_MONTH (22 weekdays).
  // Only meaningful when salaryConfigured; the zero override below
  // ensures we never display a fallback-derived figure to the user.
  const dailyRate = salaryConfigured
    ? dailyRateFromSalary(salaryNumber)
    : 0;

  // Per-employee payroll deductions (SSS/PhilHealth/Pag-IBIG/Insurance/
  // custom others). Stored as JSON on Employee.deductionsJson by the
  // People → Edit Employee form.
  const payrollDeductions = useMemo(() => {
    const raw = selectedEmployee?.deductionsJson;
    if (!raw) return null;
    try { return typeof raw === "string" ? JSON.parse(raw) : raw; }
    catch { return null; }
  }, [selectedEmployee]);

  // Per-employee extra earnings (incentives, allowances, …) — stored
  // as a JSON array on Employee.earningsJson by the People form.
  const extraEarnings = useMemo(() => {
    const raw = selectedEmployee?.earningsJson;
    if (!raw) return [];
    try {
      const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  }, [selectedEmployee]);

  // Effective work settings — resolves the employee's nullable
  // override fields against the company defaults from /api/settings.
  // Only the applyDeductionsOnDayOff flag is needed here; the rest
  // are already baked into each timesheet row.
  const companyDefaults = useCompanyDefaults();
  const effectiveSettings = useMemo(
    () => resolveEmployeeWorkSettings(selectedEmployee, companyDefaults),
    [selectedEmployee, companyDefaults],
  );

  const payroll = useMemo(
    () => {
      // Hard short-circuit: an unconfigured salary returns an explicitly
      // zero breakdown. We do NOT delegate to computePayroll here because
      // its own internal fallback (Number(dailyRate) > 0 ? ... :
      // DEFAULT_DAILY_RATE) would re-inject the ₱700/day default and the
      // phantom paycheck would come right back. The banner below
      // explains the zeros to the viewer so they aren't a mystery.
      if (!salaryConfigured) {
        return {
          salaryType: effectiveSettings.salaryType || "monthly",
          dailyRate: 0,
          hourlyRate: 0,
          daysWorked: 0,
          daysAbsent: 0,
          regularPay: 0,
          overtimePay: 0,
          specialHolidayPay: 0,
          regularHolidayPay: 0,
          extraEarnings: [],
          extraEarningsSum: 0,
          gross: 0,
          lateDeduction: 0,
          undertimeDeduction: 0,
          absentDeduction: 0,
          sssDeduction: 0,
          philhealthDeduction: 0,
          pagibigDeduction: 0,
          insuranceDeduction: 0,
          otherDeductions: [],
          otherDeductionsSum: 0,
          payrollDeductionsTotal: 0,
          totalDeductions: 0,
          netPay: 0,
        };
      }
      return computePayroll(
        timesheets,
        calendarDays,
        dailyRate,
        Number(selectedYear),
        Number(selectedMonth),
        payrollDeductions,
        extraEarnings,
        effectiveSettings.applyDeductionsOnDayOff,
        // "monthly" salaried employees get their full base regardless of
        // clock-ins; only unpaid absences prorate it. "daily" employees
        // keep the per-day-worked semantics. Falls back to the company
        // default when the employee has no per-row override.
        effectiveSettings.salaryType,
      );
    },
    [salaryConfigured, timesheets, calendarDays, dailyRate, selectedYear, selectedMonth, payrollDeductions, extraEarnings, effectiveSettings.applyDeductionsOnDayOff, effectiveSettings.salaryType],
  );

  // Derived UI flag — drives the mode-aware copy in the rate badge,
  // the empty-timesheet banner suppression, and any future "Monthly
  // Salaried" badges. computePayroll echoes the resolved type back
  // so we read it from the result instead of recomputing the fallback
  // chain in two places.
  const isMonthly = payroll?.salaryType === "monthly";

  return (
    <div className={styles.center}>
      <div className={styles.centerTop}>
        <IconButton title="Pin" icon="pin" variant="circle" />

        <div className={styles.searchWrap}>
          <Icon name="search" />
          <input className={styles.searchInput} placeholder="Search" />
        </div>

        <IconButton title="Filter" icon="filter" variant="circle" />
        <IconButton title="Add" icon="plus" variant="circle" />
      </div>

      <div className={styles.centerStats}>
        <div className={styles.hoursBig}>
          <span>Based Salary</span> {salaryHidden ? "••••••" : salary}
          <button
            type="button"
            className={styles.salaryToggle}
            onClick={toggleSalary}
            aria-label={salaryHidden ? "Show salary" : "Hide salary"}
            title={salaryHidden ? "Show salary" : "Hide salary"}
          >
            {salaryHidden ? (
              // eye-off
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a19.77 19.77 0 0 1 5.06-5.94" />
                <path d="M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a19.77 19.77 0 0 1-3.17 4.19" />
                <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                <line x1="1" y1="1" x2="23" y2="23" />
              </svg>
            ) : (
              // eye
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            )}
          </button>
        </div>
        <select
          className={styles.monthPill}
          value={selectedMonth}
          onChange={(e) => onMonthChange(e.target.value)}
        >
          <option value="0">January</option>
          <option value="1">February</option>
          <option value="2">March</option>
          <option value="3">April</option>
          <option value="4">May</option>
          <option value="5">June</option>
          <option value="6">July</option>
          <option value="7">August</option>
          <option value="8">September</option>
          <option value="9">October</option>
          <option value="10">November</option>
          <option value="11">December</option>
        </select>
        <button
          type="button"
          className={styles.payslipBtn}
          onClick={() =>
            selectedEmployee && salaryConfigured && exportPayslipPDF(
              selectedEmployee,
              payroll,
              Number(selectedMonth),
              Number(selectedYear),
            )
          }
          disabled={!selectedEmployee || !salaryConfigured}
          title={
            !selectedEmployee
              ? "Select an employee"
              : !salaryConfigured
                ? "Set a base salary in People → Edit Employee before generating a payslip"
                : "Generate PDF payslip"
          }
        >
          ⬇ Download Payslip
        </button>
      </div>

      <div className={styles.bars}>
        <div className={styles.barCard}>
          <div className={styles.barLabel}>Worked</div>
          <div className={`${styles.barPill} ${styles.yellowPill}`}>
            <div className={`${styles.barFill} ${styles.fillYellow}`} style={{ width: `${Math.min(100, (timesheetSummary.worked / 200) * 100)}%` }} />
            <div className={styles.barText}>{timesheetSummary.worked} hrs</div>
          </div>
        </div>

        <div className={styles.barCard}>
          <div className={styles.barLabel}>Overtime</div>
          <div className={`${styles.barPill} ${styles.darkPill}`}>
            <div className={`${styles.barFill} ${styles.fillDark}`} style={{ width: `${Math.min(100, (timesheetSummary.overtime / 50) * 100)}%` }} />
            <div className={`${styles.barText} ${styles.barTextWhite}`}>{timesheetSummary.overtime} hrs</div>
          </div>
        </div>

        <div className={styles.barCard}>
          <div className={styles.barLabel}>Leave</div>
          <div className={styles.barPill}>
            <div className={`${styles.barFill} ${styles.fillGrey}`} style={{ width: `${Math.min(100, (timesheetSummary.leave / 80) * 100)}%` }} />
            <div className={styles.barText}>{timesheetSummary.leave} hrs</div>
          </div>
        </div>
      </div>

      {/* ── Empty-timesheet info banner ────────────────────────────
          Daily-mode employees: shown when zero days have been credited
          for the period — explains why every row below is ₱0 and
          offers a one-click jump to the Timesheet page.
          Monthly-mode employees: skipped entirely. Their pay accrues
          regardless of clocked days, so a "no timesheets" warning
          would be misleading and visually noisy. (HR can still log
          attendance from the Timesheet page directly if they need
          to record absences for proration.) */}
      {selectedEmployee && payroll.daysWorked === 0 && !isMonthly && (
        <div className={styles.emptyTsBanner} role="status" aria-live="polite">
          <div className={styles.emptyTsIcon} aria-hidden>📋</div>
          <div className={styles.emptyTsBody}>
            <div className={styles.emptyTsTitle}>
              No timesheet entries yet for{" "}
              {MONTH_NAMES[Number(selectedMonth) - 1] || "this month"}
              {" "}{selectedYear || ""}
            </div>
            <div className={styles.emptyTsText}>
              Pay accrues as days are clocked in. Open Timesheet to
              start logging <b>Present</b>, <b>Holiday</b>, or
              {" "}<b>Leave</b> days for this employee — every entry
              you save here will recompute the payroll on this page.
            </div>
          </div>
          <button
            type="button"
            className={styles.emptyTsCta}
            onClick={() => {
              // Carry the selected employee + period into Timesheet
              // via query params. The Timesheet page can read them to
              // pre-select; if it doesn't yet, the params do no harm.
              const qs = new URLSearchParams();
              if (selectedEmployee?.employeeCode) qs.set("employeeCode", selectedEmployee.employeeCode);
              if (selectedYear)  qs.set("year",  String(selectedYear));
              if (selectedMonth) qs.set("month", String(selectedMonth));
              const q = qs.toString();
              navigate(q ? `/timesheet?${q}` : "/timesheet");
            }}
            title="Open the Timesheet page to log days for this employee"
          >
            <span aria-hidden>📝</span>
            <span>Open Timesheet</span>
            <span aria-hidden className={styles.emptyTsArrow}>→</span>
          </button>
        </div>
      )}

      {/* ── Salary-not-configured warning ───────────────────────────
          Shown whenever an employee is selected but their base salary
          is unset / zero / non-numeric on the Employee record. The
          banner explains the zero-everything Payroll Computation card
          below so HR doesn't mistake it for a system glitch, and
          points them at the exact place to fix it. Disables the
          "Download Payslip" CTA up top symmetrically — a payslip for
          an unconfigured salary would be misleading on paper. */}
      {selectedEmployee && !salaryConfigured && (
        <div className={styles.noSalaryBanner} role="alert" aria-live="polite">
          <div className={styles.noSalaryIcon} aria-hidden>⚠</div>
          <div className={styles.noSalaryBody}>
            <div className={styles.noSalaryTitle}>
              Base salary not configured for {selectedEmployee?.name || "this employee"}
            </div>
            <div className={styles.noSalaryText}>
              Payroll is shown as ₱0 until a base salary is set. Open{" "}
              <b>People → Edit Employee</b> and fill in the <b>Salary</b>{" "}
              field, then return here.
            </div>
          </div>
        </div>
      )}

      {/* ── Payroll Computation ────────────────────────────────── */}
      <div className={styles.payrollCard}>
        <div className={styles.payrollHeader}>
          <button
            type="button"
            className={styles.payrollToggle}
            onClick={togglePayroll}
            aria-expanded={!payrollCollapsed}
            aria-controls="payroll-rows"
            title={payrollCollapsed ? "Show details" : "Hide details"}
          >
            <span className={`${styles.chev} ${payrollCollapsed ? styles.chevCollapsed : ""}`}>▾</span>
            <span className={styles.payrollTitle}>Payroll Computation</span>
          </button>
          {/* Mode-aware status pill.
              Monthly: "Monthly Salaried · ₱X base · N days absent" —
              days worked is largely cosmetic since pay is fixed; what
              the parent / HR cares about is the absent count that
              prorated the base. The badge picks up an emerald accent
              so the salaried mode reads as "this employee is on
              fixed pay" at a glance.
              Daily: "Daily Rate ₱X · N days worked" — original copy. */}
          {isMonthly ? (
            <span className={`${styles.rateBadge} ${styles.rateBadgeMonthly}`}>
              <span className={styles.rateBadgeMode}>MONTHLY SALARIED</span>
              <span aria-hidden>•</span>
              <span>{peso(payroll.dailyRate * 22 /* WORK_DAYS_PER_MONTH */)} base</span>
              <span aria-hidden>•</span>
              <span>{payroll.daysAbsent} day{payroll.daysAbsent === 1 ? "" : "s"} absent</span>
            </span>
          ) : (
            <span className={styles.rateBadge}>
              Daily Rate {peso(payroll.dailyRate)}  •  {payroll.daysWorked} day{payroll.daysWorked === 1 ? "" : "s"} worked
            </span>
          )}
        </div>

        {!payrollCollapsed && (
          <div id="payroll-rows" className={styles.payrollRows}>
            <div className={styles.row}>
              <span className={styles.rowLabel}>Regular Pay</span>
              <span className={styles.rowValue}>{peso(payroll.regularPay)}</span>
            </div>
            <div className={styles.row}>
              <span className={styles.rowLabel}>Overtime (+25%/hr)</span>
              <span className={styles.rowValue}>{peso(payroll.overtimePay)}</span>
            </div>
            <div className={styles.row}>
              <span className={styles.rowLabel}>Special Holiday (+30%)</span>
              <span className={styles.rowValue}>{peso(payroll.specialHolidayPay)}</span>
            </div>
            <div className={styles.row}>
              <span className={styles.rowLabel}>Regular Holiday (+100%)</span>
              <span className={styles.rowValue}>{peso(payroll.regularHolidayPay)}</span>
            </div>
            {(payroll.extraEarnings || []).map((o, i) => (
              <div key={`earn-${i}`} className={styles.row}>
                <span className={styles.rowLabel}>{o.name}</span>
                <span className={styles.rowValue}>{peso(o.value)}</span>
              </div>
            ))}
            <div className={styles.row}>
              <span className={styles.rowLabel}>Late Deduction</span>
              <span className={styles.rowValueNeg}>− {peso(payroll.lateDeduction)}</span>
            </div>
            <div className={styles.row}>
              <span className={styles.rowLabel}>Undertime Deduction</span>
              <span className={styles.rowValueNeg}>− {peso(payroll.undertimeDeduction)}</span>
            </div>
            <div className={styles.row}>
              <span className={styles.rowLabel}>Absent Deduction</span>
              <span className={styles.rowValueNeg}>− {peso(payroll.absentDeduction)}</span>
            </div>
            <div className={styles.row}>
              <span className={styles.rowLabel}>Days Absent</span>
              <span className={styles.rowValue}>{payroll.daysAbsent}</span>
            </div>

            <div className={styles.row}>
              <span className={styles.rowLabel}>SSS</span>
              <span className={styles.rowValueNeg}>− {peso(payroll.sssDeduction)}</span>
            </div>
            <div className={styles.row}>
              <span className={styles.rowLabel}>PhilHealth</span>
              <span className={styles.rowValueNeg}>− {peso(payroll.philhealthDeduction)}</span>
            </div>
            <div className={styles.row}>
              <span className={styles.rowLabel}>Pag-IBIG</span>
              <span className={styles.rowValueNeg}>− {peso(payroll.pagibigDeduction)}</span>
            </div>
            <div className={styles.row}>
              <span className={styles.rowLabel}>Insurance</span>
              <span className={styles.rowValueNeg}>− {peso(payroll.insuranceDeduction)}</span>
            </div>
            {(payroll.otherDeductions || []).map((o, i) => (
              <div key={`other-${i}`} className={styles.row}>
                <span className={styles.rowLabel}>{o.name}</span>
                <span className={styles.rowValueNeg}>− {peso(o.value)}</span>
              </div>
            ))}
          </div>
        )}

        <div className={styles.payrollTotals}>
          <div className={styles.totalBlock}>
            <span className={styles.totalLabel}>Gross Pay</span>
            <span className={styles.totalValue}>{peso(payroll.gross)}</span>
          </div>
          <div className={styles.totalBlock}>
            <span className={styles.totalLabel}>Total Deductions</span>
            <span className={styles.totalValue}>{peso(payroll.totalDeductions)}</span>
          </div>
          <div className={styles.totalBlock}>
            <span className={styles.totalLabel}>Net Pay</span>
            <span className={styles.netValue}>{peso(payroll.netPay)}</span>
          </div>
        </div>
      </div>

      <Calendar
        calendarDays={calendarDays}
        selectedMonth={selectedMonth}
        selectedYear={selectedYear}
      />
    </div>
  );
}
